import { useState, useMemo } from 'react';
import HourglassDisabledIcon from '@mui/icons-material/HourglassDisabled';
import { useQuery, useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';

import {
  VaultExportFileModel,
  getVaultExportFiles,
  downloadVaultExportToDrive,
  VaultKeys,
} from '../../../api/vault-api';
import SharedFilePicker from '../../../shared/shared-file-picker';
import SharedButton from '../../../shared/buttons/shared-button';
import ModelDetailsDisplay from '../../../shared/model-details-display';
import { ExtendedVaultExportModel } from './vault-export-grid';
import SimpleDrawer from '../../../shared/simple-drawer';
import SharedDataGridClient from '../../../shared/shared-data-grid-client';
import { GridColDef } from '@mui/x-data-grid-pro';
import { createGridOverlays } from '../../grid-overlay/no-data-overlay-helpers';
import SharedToolbar from '../../../shared/shared-toolbar-latest';
import { useStore } from '../../state/zustand-store';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import {
  isAbleToSeeUIElement,
  VaultRoles,
} from '../../../utils/roles/client-roles';
import { useUserSettings } from '../../../utils/use-user-settings';
import { Messages } from '../../../utils/message-constants';
import { formatBytes } from '../../../utils/vault-utils';
import StatusLabel from '../../../shared/status-label';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Download from '@mui/icons-material/Download';
import AttachEmail from '@mui/icons-material/AttachEmail';
import Check from '@mui/icons-material/Check';

interface VaultExportDrawerDetailsProps {
  vaultExport: ExtendedVaultExportModel;
  onClose: () => void;
  matterName?: string;
}

function FileGridToolbar() {
  return <SharedToolbar noColumn noFilter />;
}

const formatEnumString = (val?: string) => {
  if (!val) return '';
  return val
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export default function VaultExportDrawerDetailsOld({
  vaultExport,
  onClose,
  matterName,
}: Readonly<VaultExportDrawerDetailsProps>) {
  const { setSnackbar } = useStore();
  const { changeLogRoles, toolsRoles } = useUserSettings();
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [destinationFolder, setDestinationFolder] = useState<string>('');
  const [destinationFolderId, setDestinationFolderId] = useState<string>('');
  const [pendingOpenPicker, setPendingOpenPicker] = useState<
    (() => void) | null
  >(null);

  const daysUntilDeletionText = useMemo(() => {
    const createDate = dayjs(vaultExport.createTime);
    const expiryDate = createDate.add(15, 'day');
    const diffDays = expiryDate.diff(dayjs(), 'day');
    return diffDays <= 0
      ? 'Expired'
      : `${diffDays} ${diffDays === 1 ? 'day' : 'days'}`;
  }, [vaultExport.createTime]);

  const exportDetailsData = useMemo(() => {
    const data: Record<string, React.ReactNode> = {};

    if (matterName) {
      data['Matter Name'] = matterName;
    }
    if (vaultExport.corpus) {
      data['Corpus'] = formatEnumString(vaultExport.corpus);
    }
    if (vaultExport.dataScope) {
      data['Data Scope'] = formatEnumString(vaultExport.dataScope);
    }
    if (vaultExport.searchMethod) {
      data['Search Method'] = formatEnumString(vaultExport.searchMethod);
    }
    if (vaultExport.searchMethodTarget) {
      const label =
        vaultExport.searchMethod === 'ACCOUNT'
          ? 'Account Email'
          : 'Search Target';
      data[label] = vaultExport.searchMethodTarget;
    }
    if (vaultExport.timeZone) {
      data['Time Zone'] = vaultExport.timeZone;
    }
    if (vaultExport.exportFormat) {
      data['Export Format'] = vaultExport.exportFormat;
    }
    if (
      vaultExport.totalArtifactCount !== undefined &&
      vaultExport.totalArtifactCount !== null
    ) {
      data['Count'] = vaultExport.totalArtifactCount.toLocaleString();
    }
    if (
      vaultExport.dataSizeBytes !== undefined &&
      vaultExport.dataSizeBytes !== null
    ) {
      data['Data Size'] = formatBytes(vaultExport.dataSizeBytes);
    }
    if (vaultExport.terms) {
      data['Search Terms'] = vaultExport.terms;
    }
    if (vaultExport.startTime) {
      data['Start Time'] = dayjs(vaultExport.startTime).format('M/D/YY');
    }
    if (vaultExport.endTime) {
      data['End Time'] = dayjs(vaultExport.endTime).format('M/D/YY');
    }

    return data;
  }, [vaultExport, matterName]);

  const isCompleted = vaultExport.status === 'COMPLETED';

  const { data: files, isLoading: isLoadingFiles } = useQuery({
    queryKey: [
      VaultKeys.VaultExportFiles,
      vaultExport.matterId,
      vaultExport.id,
    ],
    queryFn: () => getVaultExportFiles(vaultExport.matterId, vaultExport.id),
    enabled: !!vaultExport?.id && isCompleted,
  });

  const downloadMutation = useMutation({
    mutationFn: (vars?: { folder?: string; folderId?: string }) => {
      const fName = vars?.folder ?? destinationFolder;
      const fId = vars?.folderId ?? destinationFolderId;
      return downloadVaultExportToDrive(
        vaultExport.matterId,
        vaultExport.id,
        {
          filesToDownload: selectedFiles,
        },
        fName,
        fId,
        changeLogMessage
      );
    },
    onSuccess: () => {
      setSnackbar({
        open: true,
        message: 'Vault export to drive is now running.',
        severity: 'info',
      });
      setOpenChangeLogDialog(false);
      setChangeLogMessage('');
      onClose();
    },
    onError: (error) => {
      console.error('Download failed:', error);
      setSnackbar({
        open: true,
        message: 'There was an issue starting your download.',
        severity: 'error',
      });
    },
  });

  const columns: GridColDef<VaultExportFileModel>[] = useMemo(
    () => [
      {
        field: 'fileName',
        headerName: 'File Name',
        flex: 1,
      },
      {
        field: 'size',
        headerName: 'Size',
        width: 150,
        valueFormatter: (params: number) => formatBytes(params),
      },
    ],
    []
  );

  const getSpecificDownloadRole = (corpus?: string) => {
    switch (corpus) {
      case 'MAIL':
        return VaultRoles.DownloadGmailVaultExport.value;
      case 'HANGOUTS_CHAT':
        return VaultRoles.DownloadChatVaultExport.value;
      case 'GEMINI':
        return VaultRoles.DownloadGeminiVaultExport.value;
      case 'CALENDAR':
        return VaultRoles.DownloadCalendarVaultExport.value;
      case 'DRIVE':
        return VaultRoles.DownloadDriveVaultExport.value;
      case 'GROUPS':
        return VaultRoles.DownloadGroupsVaultExport.value;
      default:
        return undefined;
    }
  };

  const getSpecificDownloadRoleName = (corpus?: string) => {
    switch (corpus) {
      case 'MAIL':
        return VaultRoles.DownloadGmailVaultExport.name;
      case 'HANGOUTS_CHAT':
        return VaultRoles.DownloadChatVaultExport.name;
      case 'GEMINI':
        return VaultRoles.DownloadGeminiVaultExport.name;
      case 'CALENDAR':
        return VaultRoles.DownloadCalendarVaultExport.name;
      case 'DRIVE':
        return VaultRoles.DownloadDriveVaultExport.name;
      case 'GROUPS':
        return VaultRoles.DownloadGroupsVaultExport.name;
      default:
        return undefined;
    }
  };

  const specificDownloadRole = getSpecificDownloadRole(vaultExport.corpus);
  const specificDownloadRoleName = getSpecificDownloadRoleName(
    vaultExport.corpus
  );

  const canDownload =
    isAbleToSeeUIElement(toolsRoles, VaultRoles.DownloadVaultExport.value) ||
    (!!specificDownloadRole &&
      isAbleToSeeUIElement(toolsRoles, specificDownloadRole));

  const hasChangeLogEnabled =
    isAbleToSeeUIElement(
      changeLogRoles,
      VaultRoles.DownloadVaultExport.value
    ) ||
    (!!specificDownloadRole &&
      isAbleToSeeUIElement(changeLogRoles, specificDownloadRole));

  const tooltipTitle = useMemo(() => {
    if (!canDownload) {
      if (specificDownloadRoleName) {
        return Messages.Errors.Permission.ActingMultiple([
          VaultRoles.DownloadVaultExport.name,
          specificDownloadRoleName,
        ]);
      }
      return Messages.Errors.Permission.Acting(
        VaultRoles.DownloadVaultExport.name
      );
    }
    if (selectedFiles.length === 0) {
      return 'You must select at least one file to download.';
    }
    return '';
  }, [canDownload, specificDownloadRoleName, selectedFiles.length]);

  function handleFolderSelected(folderId: string, folderName: string) {
    setDestinationFolder(folderName);
    setDestinationFolderId(folderId);
    downloadMutation.mutate({ folder: folderName, folderId });
  }

  function handleDownload(openDrivePicker: () => void) {
    if (!canDownload) return;
    if (hasChangeLogEnabled) {
      setPendingOpenPicker(() => openDrivePicker);
      setOpenChangeLogDialog(true);
    } else {
      openDrivePicker();
    }
  }

  return (
    <>
      <SimpleDrawer title="Export Details" onClose={onClose}>
        {/* Title Block - aligned with rules-engine-drawer-details */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {vaultExport.name || 'N/A'}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            {vaultExport.status && (
              <StatusLabel
                status={
                  vaultExport.status === 'IN_PROGRESS'
                    ? 'INPROGRESS'
                    : (vaultExport.status as any)
                }
              />
            )}
          </Stack>
        </Stack>

        {/* Basic Info Time Stack - aligned with rules-engine-drawer-details */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 1, sm: 2 }}
          sx={{ mb: 2, alignItems: 'center' }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ color: 'text.secondary' }}
          >
            <Check fontSize="small" />
            <Typography variant="body2" color="inherit">
              Created {dayjs(vaultExport.createTime).format('M/D/YY, h:mm A')}
            </Typography>
          </Stack>
          <Typography sx={{ px: 1, color: 'text.secondary', fontSize: 14 }}>
            •
          </Typography>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ color: 'text.secondary' }}
          >
            <HourglassDisabledIcon fontSize="small" />
            <Typography variant="body2" color="inherit">
              Days until deletion: {daysUntilDeletionText}
            </Typography>
          </Stack>
        </Stack>

        <Divider sx={{ mb: 2 }} />

        {/* Outer stack container for spacing */}
        <Stack spacing={3}>
          <ModelDetailsDisplay
            title="Basic Information"
            data={exportDetailsData}
          />

          <Divider />

          {/* Files Section */}
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              Downloadable Files
            </Typography>
            {isCompleted ? (
              <Box sx={{ height: files && files.length > 0 ? 'auto' : 280 }}>
                <SharedDataGridClient
                  columns={columns}
                  rows={files || []}
                  loading={isLoadingFiles}
                  getRowId={(row) => row.objectName}
                  checkboxSelection
                  selectedRows={selectedFiles}
                  setSelectedRows={setSelectedFiles}
                  slots={{
                    toolbar: FileGridToolbar,
                    ...createGridOverlays('Files', <AttachEmail />),
                  }}
                />
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Files are not available. This export has not completed or has
                failed.
              </Typography>
            )}
          </Box>

          {/* Download Action Area */}
          {isCompleted && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Tooltip title={tooltipTitle} arrow>
                <span>
                  <SharedFilePicker
                    pickerType="folder"
                    selectedFileId={destinationFolderId}
                    selectedFileName={destinationFolder}
                    onFileSelected={(doc) =>
                      handleFolderSelected(doc.id, doc.name)
                    }
                    onFileRemoved={() => {
                      setDestinationFolder('');
                      setDestinationFolderId('');
                    }}
                    includeSharedDrives
                    customTrigger={(openDrivePicker) => (
                      <SharedButton
                        variant="primary"
                        color="primary"
                        startIcon={<Download />}
                        onClick={() => handleDownload(openDrivePicker)}
                        disabled={
                          !canDownload ||
                          selectedFiles.length === 0 ||
                          downloadMutation.isPending
                        }
                      >
                        Download Selected
                      </SharedButton>
                    )}
                  />
                </span>
              </Tooltip>
            </Box>
          )}
        </Stack>
      </SimpleDrawer>

      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => {
          setOpenChangeLogDialog(false);
          setPendingOpenPicker(null);
        }}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() => {
          setOpenChangeLogDialog(false);
          pendingOpenPicker?.();
          setPendingOpenPicker(null);
        }}
        mutateLoading={downloadMutation.isPending}
      />
    </>
  );
}
